/**
 * Pure image-search ranking for the browser (ticket 09). Builds searchable
 * candidates from the in-memory mirrors and ranks them with the *shared*
 * `rankImageEmbeddings` from `@egalregal/shared`, so the browser and the server
 * (ticket 10) return the same order for the same query. No model runs here and
 * no heavy dependency is imported — the WASM encoder lives in
 * `./imageSearch`'s {@link UformImageSearchService}, so these helpers stay
 * trivially unit-testable.
 */

import {
  entryPathLabelOf,
  normalizeVector,
  rankImageEmbeddings,
  type Container,
  type Item,
  type PhotoResponse,
  type RankableImage,
} from "@egalregal/shared";

/** A photo eligible for image search, carrying its stored embedding and tile. */
export interface ImageSearchCandidate extends RankableImage {
  photo: PhotoResponse;
}

/** Embeds a query into a raw 256-dim vector (the encoder wrapper is injected). */
export type EmbedQuery = (text: string) => Promise<number[]>;

/**
 * Collect every photo that has a stored embedding from the in-memory mirrors:
 * container photo files (`files[].data.embedding`) and item photos
 * (`photos[].embedding`, including `nutrition`). Deleted containers and photos
 * without an embedding are skipped — the CDC pipeline (ticket 08) has not
 * embedded them yet.
 *
 * @param containers the readable container union (ticket 12), so shared
 *   container photos rank alongside own ones.
 * @param items the mirrored item universe (unchanged).
 * @param entryLabelOf optional catalog entry display name per container, so a
 *   tile's path carries its entry root (`Garage / Regal / Kiste`).
 */
export function collectImageCandidates(
  containers: readonly Container[],
  items: readonly Item[],
  entryLabelOf?: (containerId: string) => string | null,
): ImageSearchCandidate[] {
  const candidates: ImageSearchCandidate[] = [];

  for (const container of containers) {
    if (container.deleted) continue;
    const containerPath = entryPathLabelOf(
      containers,
      container.id,
      entryLabelOf?.(container.id),
    );
    for (const file of container.files) {
      const embedding = file.data?.embedding;
      if (!embedding) continue;
      candidates.push({
        photo: {
          containerId: container.id,
          containerName: container.name,
          containerPath,
          file,
        },
        vector: embedding.vector,
        name: file.name,
        createdAt: file.createdAt,
      });
    }
  }

  for (const item of items) {
    for (const photo of item.photos ?? []) {
      const embedding = photo.embedding;
      if (!embedding) continue;
      candidates.push({
        photo: {
          itemId: item.id,
          itemName: item.name,
          itemPhotoType: photo.type,
          photoUrl: photo.url,
          photoId: photo.id,
        },
        vector: embedding.vector,
        name: item.name,
        createdAt: photo.createdAt,
      });
    }
  }

  return candidates;
}

/**
 * Embed a query and rank candidates with the shared cosine ranking. The query
 * vector is L2-normalized here (the encoder emits a raw vector — tickets 02/04),
 * matching the unit-normalized stored embeddings, so cosine is a plain dot
 * product. Returns the ranked `PhotoResponse` tiles, already cut off and sorted.
 */
export async function searchImagesByAppearance(
  query: string,
  candidates: readonly ImageSearchCandidate[],
  embed: EmbedQuery,
  options: { cutoff?: number } = {},
): Promise<PhotoResponse[]> {
  const queryVector = normalizeVector(await embed(query));
  return rankImageEmbeddings(queryVector, candidates, options).map((ranked) => ranked.item.photo);
}
